"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidImportModal/PortfolioAccountsModal";

import { Icon } from "@ospweb/sdk-ui";
import { AddAccountRow } from "./AddAccountRow";
import { ConnectionsSection } from "./ConnectionsSection";
import { PlaidInstitutionRebindDialog } from "./PlaidInstitutionRebindDialog";
import type { PortfolioAccountsModalContentProps } from "./types";
import React from "react";
import { cn } from "utils";
import { usePlaidConnectionsPanel } from "./usePlaidConnectionsPanel";

const I18n = I18nUtil.createScoped(Translations);

export const PortfolioAccountsModalContent: React.FC<
  PortfolioAccountsModalContentProps
> = ({
  onBindSuccess,
  onAddAccount,
  onClose,
  iconName,
  className,
  headerClassName,
  iconBtnClassName,
  safeAreaTop = true,
}) => {
  const {
    investConnections,
    nonInvestConnections,
    linkLoading,
    binding,
    webLinkHost,
    rebindUpdateLinkToken,
    handleRebindCancel,
    handleRebindAdd,
    onUpdateConnection,
  } = usePlaidConnectionsPanel({
    mode: "default",
    onBindSuccess,
    onClose,
  });

  const actionDisabled = linkLoading || binding;

  return (
    <div
      className={cn(
        "relative flex min-h-0 w-full h-full flex-col md:gap-8 pb-safe md:pb-0",
        safeAreaTop && "pt-safe md:pt-0",
        className,
      )}
      data-testid="portfolio-accounts-modal-content"
    >
      {webLinkHost}
      {rebindUpdateLinkToken ? (
        <PlaidInstitutionRebindDialog
          onCancel={handleRebindCancel}
          onAdd={handleRebindAdd}
        />
      ) : null}
      <h2
        className={cn(
          "shrink-0 px-4 py-2 md:pt-4 title_xl text-content_primary md:px-0 md:pb-0 gap-3 flex flex-row items-center",
          headerClassName,
        )}
      >
        <button
          type="button"
          aria-label="Close"
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full bg-background_solid_neutral_primary shadow-[0px_2px_20px_0px_rgba(0,0,0,0.08)]",
            iconBtnClassName,
          )}
          data-testid="portfolio-accounts-modal-close"
          onClick={() => onClose({ action: "close" })}
        >
          <Icon
            name={iconName || "close"}
            size={24}
            className="text-content_primary"
          />
        </button>
        {I18n.t("My accounts")}
      </h2>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-0 md:flex-none">
        <ConnectionsSection
          investConnections={investConnections}
          nonInvestConnections={nonInvestConnections}
          disabled={actionDisabled}
          onManage={onUpdateConnection}
          onNavigateAway={() => onClose({ action: "close" })}
        />
      </div>
      <div className="flex shrink-0 flex-col gap-2 px-4 pt-2 px-4 pt-4 pb-6 md:p-0">
        <AddAccountRow
          onClick={() => {
            onAddAccount?.();
          }}
        />
      </div>
    </div>
  );
};
